import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000, // 15 second timeout to prevent infinite loading
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (email, password) => api.post('/auth/login', null, { params: { email, password } }),
  getMe: () => api.get('/auth/me'),
};

export const childrenAPI = {
  getAll: () => api.get('/children'),
  create: (data) => api.post('/children', data),
  update: (id, data) => api.put(`/children/${id}`, data),
  delete: (id) => api.delete(`/children/${id}`),
  // PIN verification system
  verifyPin: (childId, pin) => api.post(`/children/${childId}/verify-pin`, null, { params: { pin } }),
  setPin: (childId, pin) => api.put(`/children/${childId}/pin`, null, { params: { pin } }),
};

export const questsAPI = {
  getDailyQuests: (childId) => api.get(`/children/${childId}/daily-quests`),
  completeQuest: (childId, questId) => api.post(`/children/${childId}/complete-quest/${questId}`),
  getSparks: (childId) => api.get(`/children/${childId}/sparks`),
  getOrbMessage: (childId, messageType = 'welcome') => 
    api.get(`/children/${childId}/orb-message`, { params: { message_type: messageType } }),
};

export const roomsAPI = {
  getAll: () => api.get('/rooms'),
};

export const lessonsAPI = {
  getAll: (roomId) => api.get('/lessons', { params: roomId ? { room_id: roomId } : {} }),
  getPublished: (roomId) => api.get('/lessons/published', { params: roomId ? { room_id: roomId } : {} }),
  getById: (id) => api.get(`/lessons/${id}`),
  create: (data) => api.post('/lessons', data),
  update: (id, data) => api.put(`/lessons/${id}`, data),
  delete: (id) => api.delete(`/lessons/${id}`),
  seedExpanded: () => api.post('/lessons/seed-expanded'),
  // Learning Intelligence Layer APIs
  getRecommended: (childId, roomId, limit = 10) => api.get(`/lessons/recommended/${childId}`, { 
    params: { room_id: roomId, limit } 
  }),
  getMetadata: (lessonId) => api.get(`/lessons/${lessonId}/metadata`),
  updateMetadata: (lessonId, metadata) => api.put(`/lessons/${lessonId}/metadata`, metadata),
  migrateMetadata: () => api.post('/lessons/migrate-metadata'),
  getNextRecommended: (childId, realmId, currentLessonId = null) => 
    api.get(`/children/${childId}/next-lesson/${realmId}`, { 
      params: currentLessonId ? { current_lesson_id: currentLessonId } : {} 
    }),
};

// Skills Taxonomy API
export const skillsAPI = {
  getTaxonomy: () => api.get('/skills/taxonomy'),
};

// Skill Progress & Recommendations API
export const skillProgressAPI = {
  getProgress: (childId) => api.get(`/children/${childId}/skill-progress`),
  recordLessonCompletion: (childId, lessonId, questionsAnswered = 0, correctAnswers = 0) => 
    api.post(`/children/${childId}/record-lesson-completion`, null, {
      params: { lesson_id: lessonId, questions_answered: questionsAnswered, correct_answers: correctAnswers }
    }),
  getRecommendations: (childId, limit = 5) => api.get(`/children/${childId}/recommendations`, { params: { limit } }),
};

export const completionsAPI = {
  complete: (data) => api.post('/completions', data),
  getProgress: (childId) => api.get(`/children/${childId}/progress`),
};

// Learning Paths API
export const learningPathsAPI = {
  getAll: (roomId) => api.get('/learning-paths', { params: roomId ? { room_id: roomId } : {} }),
  getById: (id) => api.get(`/learning-paths/${id}`),
  create: (data) => api.post('/learning-paths', data),
  getProgress: (childId, pathId) => api.get(`/children/${childId}/path-progress/${pathId}`),
};

// Badges API
export const badgesAPI = {
  getAll: () => api.get('/badges'),
  getChildBadges: (childId) => api.get(`/children/${childId}/badges`),
};

// Avatar System API
export const avatarAPI = {
  getOptions: () => api.get('/avatar/options'),
  getChildAvatar: (childId) => api.get(`/children/${childId}/avatar`),
  updateChildAvatar: (childId, avatar) => api.put(`/children/${childId}/avatar`, avatar),
  getParentAvatar: () => api.get('/parent/avatar'),
  updateParentAvatar: (avatar) => api.put('/parent/avatar', avatar),
};

// Ask the Orb (AI Tutor) API
export const askOrbAPI = {
  ask: (question, lessonId, childId) => api.post('/ask-orb', { question, lesson_id: lessonId, child_id: childId }),
};

// Typing Dojo API
export const typingDojoAPI = {
  getLevels: () => api.get('/typing-dojo/levels'),
  getChallenge: (level) => api.get(`/typing-dojo/challenge/${level}`),
  submitResult: (childId, level, wpm, accuracy, wordsTyped) => 
    api.post('/typing-dojo/submit', null, { 
      params: { child_id: childId, level, wpm, accuracy, words_typed: wordsTyped } 
    }),
  getProgress: (childId) => api.get(`/children/${childId}/typing-progress`),
  completeDrill: (childId, drillId) => api.post(`/children/${childId}/typing-drill-complete`, null, {
    params: { drill_id: drillId }
  }),
};

// Child Progress API (for parent dashboard)
export const progressAPI = {
  getFullProgress: (childId) => api.get(`/children/${childId}/full-progress`),
  getStreak: (childId) => api.get(`/children/${childId}/streak`),
  recordActivity: (childId) => api.post(`/children/${childId}/record-activity`),
  getRank: (childId) => api.get(`/children/${childId}/rank`),
  getCompletions: (childId) => api.get(`/children/${childId}/completions-list`),
};

// Constellation Saving API
export const constellationsAPI = {
  save: (childId, data) => api.post(`/children/${childId}/constellations`, data),
  getAll: (childId) => api.get(`/children/${childId}/constellations`),
  getById: (childId, id) => api.get(`/children/${childId}/constellations/${id}`),
  update: (childId, id, data) => api.put(`/children/${childId}/constellations/${id}`, data),
  delete: (childId, id) => api.delete(`/children/${childId}/constellations/${id}`),
};

// Temple Sky API
export const skyAPI = {
  getPersonal: (childId) => api.get(`/sky/personal/${childId}`),
  getFamily: (childId) => api.get(`/sky/family/${childId}`),
  getCommunity: (limit = 50) => api.get('/sky/community', { params: { limit } }),
};

// Parent Constellation Approval API
export const parentSharesAPI = {
  getPending: () => api.get('/parent/pending-shares'),
  approve: (constellationId) => api.post(`/parent/approve-share/${constellationId}`),
  decline: (constellationId) => api.post(`/parent/decline-share/${constellationId}`),
};

// Beat Forge API
export const beatsAPI = {
  save: (childId, data) => api.post(`/children/${childId}/beats`, data),
  getAll: (childId) => api.get(`/children/${childId}/beats`),
  delete: (childId, id) => api.delete(`/children/${childId}/beats/${id}`),
};

// Song Forge API
export const songsAPI = {
  save: (childId, data) => api.post(`/children/${childId}/songs`, data),
  getAll: (childId) => api.get(`/children/${childId}/songs`),
  delete: (childId, id) => api.delete(`/children/${childId}/songs/${id}`),
};

// Story Seeds API
export const storiesAPI = {
  getPrompts: () => api.get('/story-prompts'),
  getRandomPrompt: () => api.get('/story-prompts/random'),
  save: (childId, data) => api.post(`/children/${childId}/stories`, data),
  getAll: (childId) => api.get(`/children/${childId}/stories`),
  delete: (childId, id) => api.delete(`/children/${childId}/stories/${id}`),
};

// Recess Portal API
export const recessAPI = {
  // Room info
  getRooms: () => api.get('/recess/rooms'),
  getPhrases: () => api.get('/recess/phrases'),
  getEmojis: () => api.get('/recess/emojis'),
  
  // Settings (parent control)
  getSettings: (childId) => api.get(`/children/${childId}/recess/settings`),
  updateSettings: (childId, settings) => api.put(`/children/${childId}/recess/settings`, settings),
  
  // Eligibility & Session
  checkEligibility: (childId) => api.get(`/children/${childId}/recess/eligibility`),
  enterRoom: (childId, roomId) => api.post(`/children/${childId}/recess/enter/${roomId}`),
  leaveRoom: (childId) => api.post(`/children/${childId}/recess/leave`),
  
  // Contributions
  getContributions: (roomId, limit = 50) => api.get(`/recess/rooms/${roomId}/contributions`, { params: { limit } }),
  contribute: (childId, roomId, type, content, parentId = null) => 
    api.post(`/children/${childId}/recess/rooms/${roomId}/contribute`, null, { 
      params: { contribution_type: type, content, parent_id: parentId } 
    }),
  react: (contributionId, emoji) => api.post(`/recess/contributions/${contributionId}/react`, null, { params: { emoji } }),
  
  // Story Circle specific
  getStoryPrompts: () => api.get('/recess/story-circle/prompts'),
  getCurrentStory: () => api.get('/recess/story-circle/current'),
  
  // Question Garden - Aret'Hera AI
  askQuestion: (childId, question) => api.post('/recess/question', { child_id: childId, question }),
  getQuestionHistory: (childId, limit = 20) => api.get(`/recess/question-garden/history/${childId}`, { params: { limit } }),
  
  // Garden of Wisdom - Favorite Seeds
  toggleFavorite: (entryId) => api.post(`/recess/question/${entryId}/favorite`),
  getGardenOfWisdom: (childId, limit = 50) => api.get(`/children/${childId}/garden-of-wisdom`, { params: { limit } }),
};

// Lesson Builder API (Admin)
export const lessonBuilderAPI = {
  getRealms: () => api.get('/admin/realms'),
  getTemplates: () => api.get('/admin/lessons/templates'),
  getAllLessons: () => api.get('/admin/lessons'),
  createLesson: (lessonData) => api.post('/admin/lessons/create', lessonData),
};

// Analytics API
export const analyticsAPI = {
  trackActivity: (activity) => api.post('/analytics/track', activity),
  getChildAnalytics: (childId) => api.get(`/analytics/child/${childId}`),
  getPlatformAnalytics: () => api.get('/analytics/platform'),
};

// Admin Analytics API
export const adminAPI = {
  // Analytics drill-down
  getActivityUsage: () => api.get('/admin/analytics/activity-usage'),
  getLearningAnalytics: () => api.get('/admin/analytics/learning'),
  getCreativeAnalytics: () => api.get('/admin/analytics/creative'),
  getReadingAnalytics: () => api.get('/admin/analytics/reading'),
  // Child activity view
  getChildActivity: (childId) => api.get(`/admin/children/${childId}/activity`),
  // Support tools
  getUsers: (search) => api.get('/admin/users', { params: { search } }),
  getUserDetail: (userId) => api.get(`/admin/users/${userId}`),
  resetPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  resetChildPin: (childId) => api.post(`/admin/children/${childId}/reset-pin`),
  // Plan management
  setUserPlan: (userId, plan) => api.post('/admin/set-plan', { user_id: userId, plan }),
};

// Beta Invite API (Admin)
export const betaAPI = {
  checkAdmin: () => api.get('/admin/check'),
  getInvites: () => api.get('/admin/beta-invites'),
  createInvite: (data) => api.post('/admin/beta-invites', data),
  markSent: (inviteId) => api.put(`/admin/beta-invites/${inviteId}/send`),
  revokeInvite: (inviteId) => api.put(`/admin/beta-invites/${inviteId}/revoke`),
  deleteInvite: (inviteId) => api.delete(`/admin/beta-invites/${inviteId}`),
  validateCode: (code) => api.get(`/beta/validate/${code}`),
  registerWithCode: (code, userId) => api.post('/beta/register', null, { params: { code, user_id: userId } }),
  getFeatures: () => api.get('/beta/features'),
};

// Journey API
export const journeyAPI = {
  getAllJourneys: () => api.get('/journeys'),
  getJourney: (journeyId) => api.get(`/journeys/${journeyId}`),
  getChildProgress: (childId) => api.get(`/children/${childId}/journey-progress`),
  getCurrentJourney: (childId) => api.get(`/children/${childId}/current-journey`),
  getLessonJourney: (lessonId) => api.get(`/lessons/${lessonId}/journey`),
};

// Billing API
export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  getSubscriptionStatus: () => api.get('/billing/subscription-status'),
  getFounderStatus: () => api.get('/billing/founder-status'),
  createCheckoutSession: (planId, originUrl) => api.post('/billing/create-checkout-session', {
    plan_id: planId,
    origin_url: originUrl
  }),
  getCheckoutStatus: (sessionId) => api.get(`/billing/checkout-status/${sessionId}`),
  checkActivityLimit: (childId) => api.get(`/billing/activity-limit/${childId}`),
};

export default api;
